import React, { useState, useEffect } from 'react';
import { collection, getDocs, query, where, addDoc, doc, deleteDoc } from 'firebase/firestore';
import { db } from '../firebase';
import { showSuccess, showError } from '../utils/toast';
import { GoogleGenerativeAI } from '@google/generative-ai';
import jsPDF from 'jspdf';

const AIAnalysis = () => {
  const [exams, setExams] = useState([]);
  const [loading, setLoading] = useState(false);
  const [analyzingExam, setAnalyzingExam] = useState(null);
  const [analysisData, setAnalysisData] = useState(null);
  const [viewMode, setViewMode] = useState('list'); // 'list' or 'analysis'
  const [generatingPDF, setGeneratingPDF] = useState(false);
  const [examAnalyses, setExamAnalyses] = useState({}); // Store existing analyses by examId

  const genAI = new GoogleGenerativeAI('AIzaSyAs8mrY_FQFkQiWmp0pgX7JPS2JJ9Jzav0');

  useEffect(() => {
    fetchExams();
    fetchExamAnalyses();
  }, []);

  const fetchExams = async () => {
    setLoading(true);
    try {
      const adminId = localStorage.getItem('adminId');
      const q = query(
        collection(db, 'exams'),
        where('organizationId', '==', adminId)
      );
      const querySnapshot = await getDocs(q);
      const examsList = [];
      querySnapshot.forEach((doc) => {
        examsList.push({ id: doc.id, ...doc.data() });
      });
      
      // Sort by creation date (newest first)
      examsList.sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0));
      
      setExams(examsList);
    } catch (error) {
      console.error('Error fetching exams:', error);
      showError('Failed to load exams');
    } finally {
      setLoading(false);
    }
  };

  const fetchExamAnalyses = async () => {
    try {
      const adminId = localStorage.getItem('adminId');
      const q = query(
        collection(db, 'examAnalyses'),
        where('organizationId', '==', adminId)
      );
      const querySnapshot = await getDocs(q);
      const analyses = {};
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        analyses[data.examId] = { id: doc.id, ...data };
      });
      setExamAnalyses(analyses);
    } catch (error) {
      console.error('Error fetching analyses:', error);
    }
  };

  const fetchExamResults = async (examId) => {
    try {
      const q = query(
        collection(db, 'examSubmissions'),
        where('examId', '==', examId)
      );
      const querySnapshot = await getDocs(q);
      const results = [];
      querySnapshot.forEach((doc) => {
        results.push({ id: doc.id, ...doc.data() });
      });
      return results;
    } catch (error) {
      console.error('Error fetching exam results:', error);
      return [];
    }
  };

  const analyzeExamWithAI = async (exam) => {
    setAnalyzingExam(exam.id);
    setLoading(true);
    
    try {
      // Fetch exam results
      const results = await fetchExamResults(exam.id);
      
      if (results.length === 0) {
        showError('No student submissions found for this exam');
        setLoading(false);
        setAnalyzingExam(null);
        return;
      }

      // Prepare data for AI analysis
      const totalStudents = results.length;
      const scores = results.map(r => r.score || 0);
      const averageScore = scores.reduce((a, b) => a + b, 0) / totalStudents;
      const maxScore = Math.max(...scores);
      const minScore = Math.min(...scores);
      const passRate = results.filter(r => (r.score || 0) >= (exam.totalMarks * 0.4)).length / totalStudents * 100;

      // Calculate distribution
      const scoreRanges = {
        '90-100': 0,
        '70-89': 0,
        '50-69': 0,
        '30-49': 0,
        'Below 30': 0
      };

      results.forEach(r => {
        const percentage = ((r.score || 0) / exam.totalMarks) * 100;
        if (percentage >= 90) scoreRanges['90-100']++;
        else if (percentage >= 70) scoreRanges['70-89']++;
        else if (percentage >= 50) scoreRanges['50-69']++;
        else if (percentage >= 30) scoreRanges['30-49']++;
        else scoreRanges['Below 30']++;
      });

      // Student-wise data
      const studentData = results.map(r => ({
        name: r.studentName || 'Unknown',
        email: r.studentEmail || 'N/A',
        score: r.score || 0,
        totalMarks: exam.totalMarks,
        percentage: (((r.score || 0) / exam.totalMarks) * 100).toFixed(2),
        timeTaken: r.timeTaken || 'N/A',
        submittedAt: r.submittedAt ? new Date(r.submittedAt).toLocaleString() : 'N/A'
      }));

      // Sort students by score (highest first)
      studentData.sort((a, b) => b.score - a.score);

      // Prepare detailed student list for AI
      const topStudentsCount = Math.min(5, totalStudents);
      const topStudentsList = studentData.slice(0, topStudentsCount)
        .map((s, i) => `${i + 1}. ${s.name} - ${s.score}/${exam.totalMarks} (${s.percentage}%)`)
        .join('\n');

      // Create a more detailed prompt with actual data
      const allStudentsDetails = studentData.map((s, i) => 
        `Student ${i + 1}: ${s.name} scored ${s.score}/${exam.totalMarks} (${s.percentage}%)`
      ).join('\n');

      // Prepare prompt for Gemini
      const prompt = `
You are an educational data analyst. Analyze the following REAL exam performance data and provide SPECIFIC insights based on the ACTUAL numbers provided below. Do NOT use generic statements.

EXAM DETAILS:
- Exam Name: ${exam.name}
- Total Students Appeared: ${totalStudents}
- Total Marks: ${exam.totalMarks}
- Average Score: ${averageScore.toFixed(2)} marks
- Highest Score: ${maxScore} marks
- Lowest Score: ${minScore} marks
- Pass Rate (40% threshold): ${passRate.toFixed(2)}%

SCORE DISTRIBUTION (Actual Numbers):
- 90-100% (Excellent): ${scoreRanges['90-100']} students
- 70-89% (Good): ${scoreRanges['70-89']} students
- 50-69% (Average): ${scoreRanges['50-69']} students
- 30-49% (Below Average): ${scoreRanges['30-49']} students
- Below 30% (Poor): ${scoreRanges['Below 30']} students

TOP ${topStudentsCount} PERFORMER${topStudentsCount > 1 ? 'S' : ''}:
${topStudentsList}

ALL STUDENT PERFORMANCE:
${allStudentsDetails}

IMPORTANT INSTRUCTIONS:
- Use the SPECIFIC numbers provided above in your analysis
- Reference the ACTUAL student count (${totalStudents} student${totalStudents > 1 ? 's' : ''})
- Comment on the ACTUAL score distribution shown above
- Mention the SPECIFIC average score (${averageScore.toFixed(2)}) in context
- If there's only 1 student, focus on individual performance analysis
- If there are few students (2-3), mention that the sample size is small
- For larger groups, provide class-level insights

Please provide a CUSTOMIZED analysis with:

1. **Overall Performance Summary**: 
   - State the actual number of students (${totalStudents})
   - Mention the specific average score (${averageScore.toFixed(2)}/${exam.totalMarks})
   - Comment on the actual pass rate (${passRate.toFixed(2)}%)

2. **Key Insights**: 
   - 3-5 bullet points based on the ACTUAL score distribution above
   - Mention specific percentages and numbers from the data

3. **Performance Analysis**:
   - Discuss the ACTUAL distribution of scores
   - Note which score range has the most students
   - Comment on the gap between highest (${maxScore}) and lowest (${minScore}) scores

4. **Strengths & Weaknesses**:
   - Based on the actual percentage of students in each range
   - Specific observations about student performance

5. **Actionable Recommendations**:
   - Tailored to the actual class size and performance distribution
   - Specific suggestions based on where most students are performing

Format your response with clear headings using ** marks and bullet points.
`;

      // Call Gemini API
      const model = genAI.getGenerativeModel({ model: "gemini-3-flash-preview" })
      const result = await model.generateContent(prompt);
      const aiAnalysis = result.response.text();

      // Set analysis data
      const analysisPayload = {
        exam: exam,
        statistics: {
          totalStudents,
          averageScore: averageScore.toFixed(2),
          maxScore,
          minScore,
          passRate: passRate.toFixed(2),
          scoreDistribution: scoreRanges
        },
        studentData,
        aiInsights: aiAnalysis
      };

      setAnalysisData(analysisPayload);

      // Save analysis to database
      try {
        const adminId = localStorage.getItem('adminId');
        const analysisDocData = {
          examId: exam.id,
          examName: exam.name,
          organizationId: adminId,
          analyzedAt: new Date().toISOString(),
          analyzedBy: localStorage.getItem('adminEmail'),
          ...analysisPayload
        };

        // Delete old analysis if exists
        if (examAnalyses[exam.id]) {
          await deleteDoc(doc(db, 'examAnalyses', examAnalyses[exam.id].id));
        }

        // Save new analysis
        const docRef = await addDoc(collection(db, 'examAnalyses'), analysisDocData);
        
        // Update local state
        setExamAnalyses({
          ...examAnalyses,
          [exam.id]: { id: docRef.id, ...analysisDocData }
        });
      } catch (dbError) {
        console.error('Error saving analysis to database:', dbError);
        // Don't fail the whole operation if DB save fails
      }

      setViewMode('analysis');
      showSuccess('Analysis completed successfully!');
    } catch (error) {
      console.error('Error analyzing exam:', error);
      showError('Failed to analyze exam. Please try again.');
    } finally {
      setLoading(false);
      setAnalyzingExam(null);
    }
  };

  const viewStoredAnalysis = (examId) => {
    const storedAnalysis = examAnalyses[examId];
    if (storedAnalysis) {
      setAnalysisData({
        exam: storedAnalysis.exam,
        statistics: storedAnalysis.statistics,
        studentData: storedAnalysis.studentData,
        aiInsights: storedAnalysis.aiInsights
      });
      setViewMode('analysis');
    }
  };

  const getTimeAgo = (dateString) => {
    const now = new Date();
    const past = new Date(dateString);
    const diffMs = now - past;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'just now';
    if (diffMins < 60) return `${diffMins} minute${diffMins > 1 ? 's' : ''} ago`;
    if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
    return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
  };

  const generatePDF = async () => {
    if (!analysisData) return;
    
    setGeneratingPDF(true);
    try {
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      let yPos = 20;

      // Title
      pdf.setFontSize(24);
      pdf.setTextColor(79, 70, 229); // Indigo
      pdf.text('AI Exam Analysis Report', pageWidth / 2, yPos, { align: 'center' });
      yPos += 15;

      // Exam Name
      pdf.setFontSize(16);
      pdf.setTextColor(0, 0, 0);
      pdf.text(analysisData.exam.name, pageWidth / 2, yPos, { align: 'center' });
      yPos += 10;

      // Date
      pdf.setFontSize(10);
      pdf.setTextColor(100, 100, 100);
      pdf.text(`Generated on: ${new Date().toLocaleString()}`, pageWidth / 2, yPos, { align: 'center' });
      yPos += 15;

      // Statistics Section
      pdf.setFontSize(14);
      pdf.setTextColor(79, 70, 229);
      pdf.text('Performance Statistics', 15, yPos);
      yPos += 8;

      pdf.setFontSize(10);
      pdf.setTextColor(0, 0, 0);
      const stats = [
        `Total Students: ${analysisData.statistics.totalStudents}`,
        `Average Score: ${analysisData.statistics.averageScore}/${analysisData.exam.totalMarks}`,
        `Highest Score: ${analysisData.statistics.maxScore}`,
        `Lowest Score: ${analysisData.statistics.minScore}`,
        `Pass Rate: ${analysisData.statistics.passRate}%`
      ];

      stats.forEach(stat => {
        pdf.text(stat, 20, yPos);
        yPos += 6;
      });
      yPos += 5;

      // Score Distribution
      pdf.setFontSize(14);
      pdf.setTextColor(79, 70, 229);
      pdf.text('Score Distribution', 15, yPos);
      yPos += 8;

      pdf.setFontSize(10);
      pdf.setTextColor(0, 0, 0);
      Object.entries(analysisData.statistics.scoreDistribution).forEach(([range, count]) => {
        pdf.text(`${range}%: ${count} students`, 20, yPos);
        yPos += 6;
      });
      yPos += 10;

      // Check if we need a new page
      if (yPos > pageHeight - 40) {
        pdf.addPage();
        yPos = 20;
      }

      // AI Insights
      pdf.setFontSize(14);
      pdf.setTextColor(79, 70, 229);
      pdf.text('AI-Generated Insights', 15, yPos);
      yPos += 8;

      pdf.setFontSize(9);
      pdf.setTextColor(0, 0, 0);
      const insights = analysisData.aiInsights.split('\n');
      insights.forEach(line => {
        if (yPos > pageHeight - 20) {
          pdf.addPage();
          yPos = 20;
        }
        
        const cleanLine = line.replace(/[*#]/g, '').trim();
        if (cleanLine) {
          const splitText = pdf.splitTextToSize(cleanLine, pageWidth - 30);
          splitText.forEach(text => {
            pdf.text(text, 15, yPos);
            yPos += 5;
          });
        }
      });

      // Add new page for student data
      pdf.addPage();
      yPos = 20;

      pdf.setFontSize(14);
      pdf.setTextColor(79, 70, 229);
      pdf.text('Student Performance Details', 15, yPos);
      yPos += 10;

      // Table header
      pdf.setFontSize(9);
      pdf.setTextColor(255, 255, 255);
      pdf.setFillColor(79, 70, 229);
      pdf.rect(15, yPos - 5, pageWidth - 30, 8, 'F');
      pdf.text('Rank', 20, yPos);
      pdf.text('Student Name', 35, yPos);
      pdf.text('Score', 120, yPos);
      pdf.text('Percentage', 150, yPos);
      yPos += 8;

      // Table rows
      pdf.setTextColor(0, 0, 0);
      analysisData.studentData.forEach((student, index) => {
        if (yPos > pageHeight - 20) {
          pdf.addPage();
          yPos = 20;
        }

        // Alternate row colors
        if (index % 2 === 0) {
          pdf.setFillColor(245, 245, 245);
          pdf.rect(15, yPos - 5, pageWidth - 30, 7, 'F');
        }

        pdf.text(`${index + 1}`, 20, yPos);
        pdf.text(student.name.substring(0, 30), 35, yPos);
        pdf.text(`${student.score}/${student.totalMarks}`, 120, yPos);
        pdf.text(`${student.percentage}%`, 150, yPos);
        yPos += 7;
      });

      // Save PDF
      pdf.save(`${analysisData.exam.name}_AI_Analysis.pdf`);
      showSuccess('PDF downloaded successfully!');
    } catch (error) {
      console.error('Error generating PDF:', error);
      showError('Failed to generate PDF');
    } finally {
      setGeneratingPDF(false);
    }
  };

  const formatAIText = (text) => {
    return text.split('\n').map((line, index) => {
      // Handle headers (lines with ** or ##)
      if (line.includes('**') || line.startsWith('##')) {
        const cleanLine = line.replace(/[*#]/g, '').trim();
        return (
          <h3 key={index} className="text-lg font-bold text-indigo-900 mt-4 mb-2">
            {cleanLine}
          </h3>
        );
      }
      
      // Handle bullet points
      if (line.trim().startsWith('-') || line.trim().startsWith('•')) {
        const cleanLine = line.replace(/^[-•]\s*/, '').trim();
        return (
          <li key={index} className="ml-6 mb-2 text-gray-700">
            {cleanLine}
          </li>
        );
      }
      
      // Handle numbered lists
      if (/^\d+\./.test(line.trim())) {
        return (
          <li key={index} className="ml-6 mb-2 text-gray-700">
            {line.trim()}
          </li>
        );
      }
      
      // Empty lines
      if (!line.trim()) {
        return <div key={index} className="h-2"></div>;
      }
      
      // Regular paragraphs
      return (
        <p key={index} className="text-gray-700 mb-2">
          {line}
        </p>
      );
    });
  };

  if (viewMode === 'analysis' && analysisData) {
    return (
      <div className="space-y-6">
        {/* Header */}
        <div className="bg-gradient-to-r from-indigo-600 to-purple-600 rounded-xl p-6 text-white">
          <div className="flex items-center justify-between">
            <div>
              <button
                onClick={() => {
                  setViewMode('list');
                  setAnalysisData(null);
                }}
                className="mb-3 text-white hover:text-indigo-200 transition flex items-center gap-2"
              >
                <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
                </svg>
                Back to Exams
              </button>
              <h2 className="text-3xl font-bold mb-2">AI Analysis Report</h2>
              <p className="text-indigo-100">{analysisData.exam.name}</p>
            </div>
            <button
              onClick={generatePDF}
              disabled={generatingPDF}
              className="bg-white text-indigo-600 px-6 py-3 rounded-lg font-semibold hover:bg-indigo-50 transition disabled:opacity-50 flex items-center gap-2"
            >
              {generatingPDF ? (
                <>
                  <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none"/>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"/>
                  </svg>
                  Generating...
                </>
              ) : (
                <>
                  <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  Download PDF
                </>
              )}
            </button>
          </div>
        </div>

        {/* Statistics Cards */}
        <div className="grid grid-cols-1 md:grid-cols-5 gap-6">
          <div className="bg-white rounded-xl shadow-md p-6 border-l-4 border-blue-500">
            <p className="text-sm text-gray-600 mb-1">Total Students</p>
            <p className="text-3xl font-bold text-gray-900">{analysisData.statistics.totalStudents}</p>
          </div>
          <div className="bg-white rounded-xl shadow-md p-6 border-l-4 border-green-500">
            <p className="text-sm text-gray-600 mb-1">Average Score</p>
            <p className="text-3xl font-bold text-gray-900">{analysisData.statistics.averageScore}</p>
          </div>
          <div className="bg-white rounded-xl shadow-md p-6 border-l-4 border-purple-500">
            <p className="text-sm text-gray-600 mb-1">Pass Rate</p>
            <p className="text-3xl font-bold text-gray-900">{analysisData.statistics.passRate}%</p>
          </div>
          <div className="bg-white rounded-xl shadow-md p-6 border-l-4 border-yellow-500">
            <p className="text-sm text-gray-600 mb-1">Highest Score</p>
            <p className="text-3xl font-bold text-gray-900">{analysisData.statistics.maxScore}</p>
          </div>
          <div className="bg-white rounded-xl shadow-md p-6 border-l-4 border-red-500">
            <p className="text-sm text-gray-600 mb-1">Lowest Score</p>
            <p className="text-3xl font-bold text-gray-900">{analysisData.statistics.minScore}</p>
          </div>
        </div>

        {/* Score Distribution */}
        <div className="bg-white rounded-xl shadow-md p-6">
          <h3 className="text-xl font-bold text-gray-900 mb-4">Score Distribution</h3>
          <div className="space-y-3">
            {Object.entries(analysisData.statistics.scoreDistribution).map(([range, count]) => {
              const percentage = (count / analysisData.statistics.totalStudents * 100).toFixed(1);
              return (
                <div key={range}>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-sm font-medium text-gray-700">{range}%</span>
                    <span className="text-sm text-gray-600">{count} students ({percentage}%)</span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-3">
                    <div
                      className="bg-gradient-to-r from-indigo-500 to-purple-500 h-3 rounded-full transition-all"
                      style={{ width: `${percentage}%` }}
                    ></div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* AI Insights */}
        <div className="bg-white rounded-xl shadow-md p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="bg-gradient-to-r from-indigo-600 to-purple-600 p-3 rounded-lg">
              <svg className="h-6 w-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
              </svg>
            </div>
            <h3 className="text-xl font-bold text-gray-900">AI-Generated Insights</h3>
          </div>
          <div className="prose max-w-none">
            {formatAIText(analysisData.aiInsights)}
          </div>
        </div>

        {/* Student Performance Table */}
        <div className="bg-white rounded-xl shadow-md p-6">
          <h3 className="text-xl font-bold text-gray-900 mb-4">Student Performance Details</h3>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b-2 border-gray-200">
                  <th className="text-left py-3 px-4 font-semibold text-gray-700">Rank</th>
                  <th className="text-left py-3 px-4 font-semibold text-gray-700">Student Name</th>
                  <th className="text-left py-3 px-4 font-semibold text-gray-700">Email</th>
                  <th className="text-center py-3 px-4 font-semibold text-gray-700">Score</th>
                  <th className="text-center py-3 px-4 font-semibold text-gray-700">Percentage</th>
                  <th className="text-left py-3 px-4 font-semibold text-gray-700">Time Taken</th>
                </tr>
              </thead>
              <tbody>
                {analysisData.studentData.map((student, index) => (
                  <tr
                    key={index}
                    className={`border-b border-gray-100 hover:bg-gray-50 transition ${
                      index < 3 ? 'bg-green-50' : ''
                    }`}
                  >
                    <td className="py-3 px-4">
                      <span className={`inline-flex items-center justify-center w-8 h-8 rounded-full font-bold ${
                        index === 0 ? 'bg-yellow-400 text-yellow-900' :
                        index === 1 ? 'bg-gray-300 text-gray-800' :
                        index === 2 ? 'bg-orange-400 text-orange-900' :
                        'bg-gray-100 text-gray-600'
                      }`}>
                        {index + 1}
                      </span>
                    </td>
                    <td className="py-3 px-4 font-medium text-gray-900">{student.name}</td>
                    <td className="py-3 px-4 text-gray-600 text-sm">{student.email}</td>
                    <td className="py-3 px-4 text-center">
                      <span className="font-semibold text-gray-900">
                        {student.score}/{student.totalMarks}
                      </span>
                    </td>
                    <td className="py-3 px-4 text-center">
                      <span className={`px-3 py-1 rounded-full text-sm font-semibold ${
                        student.percentage >= 90 ? 'bg-green-100 text-green-800' :
                        student.percentage >= 70 ? 'bg-blue-100 text-blue-800' :
                        student.percentage >= 50 ? 'bg-yellow-100 text-yellow-800' :
                        'bg-red-100 text-red-800'
                      }`}>
                        {student.percentage}%
                      </span>
                    </td>
                    <td className="py-3 px-4 text-gray-600 text-sm">{student.timeTaken}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-indigo-600 to-purple-600 rounded-xl p-6 text-white">
        <div className="flex items-center gap-3 mb-2">
          <div className="bg-white bg-opacity-20 p-3 rounded-lg">
            <svg className="h-8 w-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
            </svg>
          </div>
          <div>
            <h2 className="text-3xl font-bold">AI-Powered Exam Analysis</h2>
            <p className="text-indigo-100 mt-1">Get detailed insights and performance analysis using advanced AI</p>
          </div>
          <span className="ml-auto bg-yellow-400 text-yellow-900 px-4 py-2 rounded-full text-sm font-bold animate-pulse">
            NEW
          </span>
        </div>
      </div>

      {/* Exams List */}
      <div className="bg-white rounded-xl shadow-md overflow-hidden">
        <div className="p-6 border-b border-gray-200 bg-gray-50">
          <h3 className="text-xl font-bold text-gray-900">Select an Exam to Analyze</h3>
          <p className="text-gray-600 mt-1">AI will analyze student performance and provide actionable insights</p>
        </div>

        {loading && !analyzingExam ? (
          <div className="p-12 text-center">
            <svg className="animate-spin h-12 w-12 text-indigo-600 mx-auto mb-4" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none"/>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"/>
            </svg>
            <p className="text-gray-600">Loading exams...</p>
          </div>
        ) : exams.length === 0 ? (
          <div className="p-12 text-center">
            <svg className="h-16 w-16 text-gray-300 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
            <h3 className="text-xl font-bold text-gray-900 mb-2">No Exams Found</h3>
            <p className="text-gray-600">Create an exam first to use AI analysis</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-200">
            {exams.map((exam) => (
              <div key={exam.id} className="p-6 hover:bg-gray-50 transition">
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h4 className="text-lg font-bold text-gray-900">{exam.name}</h4>
                      <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                        exam.status === 'active' ? 'bg-green-100 text-green-800' :
                        exam.status === 'completed' ? 'bg-blue-100 text-blue-800' :
                        'bg-gray-100 text-gray-800'
                      }`}>
                        {exam.status?.toUpperCase()}
                      </span>
                    </div>
                    <div className="flex items-center gap-6 text-sm text-gray-600">
                      <span className="flex items-center gap-1">
                        <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        {exam.duration} mins
                      </span>
                      <span className="flex items-center gap-1">
                        <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                        </svg>
                        {exam.totalQuestions} Questions
                      </span>
                      <span className="flex items-center gap-1">
                        <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 3.055A9.001 9.001 0 1020.945 13H11V3.055z" />
                        </svg>
                        {exam.totalMarks} Marks
                      </span>
                    </div>
                    {examAnalyses[exam.id] && (
                      <div className="mt-2 flex items-center gap-2 text-sm text-indigo-600">
                        <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                        <span className="font-medium">Already analyzed {getTimeAgo(examAnalyses[exam.id].analyzedAt)}</span>
                      </div>
                    )}
                  </div>
                  <div className="flex flex-col gap-2">
                    {examAnalyses[exam.id] ? (
                      <>
                        <button
                          onClick={() => viewStoredAnalysis(exam.id)}
                          className="bg-green-600 text-white px-6 py-3 rounded-lg font-semibold hover:shadow-lg transition flex items-center gap-2"
                        >
                          <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                          </svg>
                          View Analysis
                        </button>
                        <button
                          onClick={() => analyzeExamWithAI(exam)}
                          disabled={loading && analyzingExam === exam.id}
                          className="bg-gradient-to-r from-orange-500 to-red-500 text-white px-6 py-2 rounded-lg font-semibold hover:shadow-lg transition disabled:opacity-50 flex items-center gap-2 text-sm"
                        >
                          {loading && analyzingExam === exam.id ? (
                            <>
                              <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none"/>
                                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"/>
                              </svg>
                              Re-analyzing...
                            </>
                          ) : (
                            <>
                              <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                              </svg>
                              Re-analyze
                            </>
                          )}
                        </button>
                      </>
                    ) : (
                      <button
                        onClick={() => analyzeExamWithAI(exam)}
                        disabled={loading && analyzingExam === exam.id}
                        className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white px-6 py-3 rounded-lg font-semibold hover:shadow-lg transition disabled:opacity-50 flex items-center gap-2"
                      >
                        {loading && analyzingExam === exam.id ? (
                          <>
                            <svg className="animate-spin h-5 w-5" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none"/>
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"/>
                            </svg>
                            Analyzing...
                          </>
                        ) : (
                          <>
                            <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                            </svg>
                            Analyze with AI
                          </>
                        )}
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AIAnalysis;
